import React from 'react';
import { cx } from './cx';
import { initials } from './initials';

// Saturated enough for white initials to pass contrast on each.
const COLOURS = [
  'bg-violet-600', 'bg-cyan-600', 'bg-pink-600', 'bg-emerald-600',
  'bg-amber-600', 'bg-sky-600', 'bg-rose-600', 'bg-teal-600',
];

const SIZES = { xs: 'h-5 w-5 text-[9px]', sm: 'h-6 w-6 text-[10px]', md: 'h-8 w-8 text-xs', lg: 'h-10 w-10 text-sm' };

/** The same person gets the same colour on every device, from their id. */
function colourFor(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) | 0;
  return COLOURS[Math.abs(hash) % COLOURS.length];
}

export interface AvatarProps {
  name: string;
  /** A stable id for the colour; the name is used when it's missing. */
  id?: string;
  size?: keyof typeof SIZES;
  className?: string;
}

export function Avatar({ name, id, size = 'sm', className }: AvatarProps) {
  return (
    <span
      role="img"
      aria-label={name}
      title={name}
      className={cx(
        'inline-flex flex-none select-none items-center justify-center rounded-full font-semibold text-white ring-2 ring-white dark:ring-gray-900',
        colourFor(id ?? name),
        SIZES[size],
        className,
      )}
    >
      {initials(name)}
    </span>
  );
}

/** Overlapping avatars, with "+N" for the people beyond `max`. */
export function AvatarStack({ people, max = 4, size = 'sm' }: { people: { name: string; id?: string }[]; max?: number; size?: AvatarProps['size'] }) {
  const shown = people.slice(0, max);
  const rest = people.length - shown.length;
  return (
    <span className="inline-flex -space-x-1.5">
      {shown.map((p) => <Avatar key={p.id ?? p.name} name={p.name} id={p.id} size={size} />)}
      {rest > 0 && (
        <span className={cx('inline-flex flex-none items-center justify-center rounded-full bg-gray-100 font-semibold text-gray-600 ring-2 ring-white dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-900', SIZES[size])}>
          +{rest}
        </span>
      )}
    </span>
  );
}
